import React, { useState, useEffect, useCallback } from 'react';
import { tasksAPI, taskGroupsAPI, vulnerabilitiesAPI } from '../services/api';
import { useNotification } from '../hooks/useNotification';
import { 
  PlayCircle, 
  StopCircle, 
  CheckCircle, 
  XCircle, 
  Clock, 
  AlertCircle, 
  Trash2, 
  Eye, 
  RefreshCw,
  Filter,
  Download,
  Upload,
  ArrowRight,
  Layers,
  GitBranch
} from 'lucide-react';
import { getCompatibleTools, getToolMetadata } from '../constants/toolGraph';
import CustomFlagsInput from '../components/CustomFlagsInput';
import ScannerResults from '../components/ScannerResults';
import ReactFlow, { MiniMap, Controls, Background, MarkerType } from 'reactflow';
import 'reactflow/dist/style.css';

const Tasks = () => {
  const { showSuccess, showError, showWarning, showInfo } = useNotification();
  const [groups, setGroups] = useState([]);
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [toolFilter, setToolFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Task group viewing
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupTasks, setGroupTasks] = useState([]);
  
  // Task result viewing (separate from group modal)
  const [showTaskResultModal, setShowTaskResultModal] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskResult, setTaskResult] = useState(null);
  
  // Pass to tool modal
  const [showPassModal, setShowPassModal] = useState(false);
  const [passSourceTask, setPassSourceTask] = useState(null);
  const [passTargetTool, setPassTargetTool] = useState('');
  const [passOptions, setPassOptions] = useState({});
  const [passing, setPassing] = useState(false);
  
  // Graph view modal
  const [showGraphModal, setShowGraphModal] = useState(false);
  const [graphNodes, setGraphNodes] = useState([]);
  const [graphEdges, setGraphEdges] = useState([]);

  // Polling for tasks with running status
  const [pollingTasks, setPollingTasks] = useState(new Set());
  const [autoRefreshInterval, setAutoRefreshInterval] = useState(null);

  const limit = 20;

  useEffect(() => {
    fetchTasks();
    fetchStats();
  }, [page, statusFilter, toolFilter]);

  // Auto-refresh for multi-user visibility (10-15 second interval)
  useEffect(() => {
    // Start auto-refresh when component mounts
    const interval = setInterval(() => {
      fetchTasks();
      fetchStats();
    }, 12000); // 12 seconds interval

    setAutoRefreshInterval(interval);

    // Cleanup on unmount
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, []);

  // Poll for running tasks
  useEffect(() => {
    if (pollingTasks.size === 0) return;

    const interval = setInterval(() => {
      pollingTasks.forEach(taskId => {
        pollTaskStatus(taskId);
      });
    }, 3000); // Poll every 3 seconds

    return () => clearInterval(interval);
  }, [pollingTasks]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      // Fetch task groups instead of individual tasks
      const response = await taskGroupsAPI.getGroups(page, limit, statusFilter);
      
      // Preserve task_details for expanded groups
      const newGroups = response.data.groups || [];
      const updatedGroups = newGroups.map(newGroup => {
        const existingGroup = groups.find(g => g._id === newGroup._id);
        if (existingGroup && existingGroup.task_details) {
          return { ...newGroup, task_details: existingGroup.task_details };
        }
        return newGroup;
      });
      
      setGroups(updatedGroups);
      setFilteredGroups(updatedGroups);
      setTotal(response.data.total || 0);

      // Add running/pending groups to polling set
      const running = new Set();
      updatedGroups.forEach(group => {
        if (group.status === 'running' || group.status === 'pending') {
          running.add(group._id);
        }
      });
      setPollingTasks(running);

    } catch (err) {
      setError(err.response?.data?.message || 'Не удалось получить группы пайплайнов');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await tasksAPI.getTasksStats();
      setStats(response.data);
    } catch (err) {
      console.error('Не удалось получить статистику:', err);
    }
  };

  const pollTaskStatus = async (groupId) => {
    try {
      const response = await taskGroupsAPI.getGroup(groupId);
      const group = response.data;
      const status = group.status;

      // Update group in list - preserve task_details if already present
      setGroups(prevGroups => 
        prevGroups.map(g => 
          g._id === groupId ? { ...g, status: status, task_details: group.task_details || g.task_details } : g
        )
      );

      // Also update filtered groups
      setFilteredGroups(prevGroups => 
        prevGroups.map(g => 
          g._id === groupId ? { ...g, status: status, task_details: group.task_details || g.task_details } : g
        )
      );

      // Remove from polling if completed
      if (status === 'completed' || status === 'failed' || status === 'cancelled') {
        setPollingTasks(prev => {
          const newSet = new Set(prev);
          newSet.delete(groupId);
          return newSet;
        });
        
        // Show notification
        showNotificationForGroup(group, status);
        
        // Refresh stats
        fetchStats();
      }
    } catch (err) {
      console.error('Не удалось опросить статус группы:', err);
    }
  };

  const showNotificationForGroup = (group, status) => {
    const groupName = group ? group.name : 'Пайплайн';

    const messages = {
      completed: `Пайплайн "${groupName}" успешно завершён`,
      failed: `Пайплайн "${groupName}" завершился с ошибкой`,
      cancelled: `Пайплайн "${groupName}" был отменён`
    };

    const message = messages[status] || `Статус пайплайна: ${status}`;
    
    console.log('Показываем уведомление:', message, 'Статус:', status);
    
    switch (status) {
      case 'completed':
        showSuccess(message);
        break;
      case 'failed':
        showError(message);
        break;
      case 'cancelled':
        showWarning(message);
        break;
      default:
        showInfo(message);
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Удалить этот пайплайн и все его задачи?')) return;

    try {
      await taskGroupsAPI.deleteGroup(groupId, true);
      fetchTasks();
      fetchStats();
      showSuccess('Пайплайн успешно удалён');
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось удалить пайплайн');
    }
  };

  const toggleGroupExpansion = async (groupId) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupId)) {
        newSet.delete(groupId);
      } else {
        newSet.add(groupId);
        // Load group details when expanding
        loadGroupDetails(groupId);
      }
      return newSet;
    });
  };

  const loadGroupDetails = async (groupId) => {
    try {
      const response = await taskGroupsAPI.getGroup(groupId);
      const group = response.data;
      
      // Update the group in the groups list with task details
      setGroups(prevGroups => 
        prevGroups.map(g => 
          g._id === groupId 
            ? { ...g, task_details: group.task_details || [] }
            : g
        )
      );
    } catch (err) {
      console.error('Не удалось загрузить детали группы:', err);
    }
  };

  const handleViewResult = async (task) => {
    if (!task.result_id) return;

    try {
      const response = await tasksAPI.getTaskResult(task.result_id);
      setTaskResult(response.data);
      setSelectedTask(task);
      setShowTaskResultModal(true);
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось получить результат');
    }
  };

  const handleDownloadResult = async (task) => {
    if (!task.result_id) return;

    try {
      const response = await tasksAPI.downloadTaskResult(task.result_id);
      const blob = new Blob([response.data], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `task_result_${task.tool}_${task._id}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showSuccess('Результат успешно скачан');
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось скачать результат');
    }
  };

  const handlePassToTool = (task) => {
    setPassSourceTask(task);
    setPassTargetTool('');
    setPassOptions({});
    setShowPassModal(true);
  };

  const handleConfirmPass = async () => {
    if (!passTargetTool) {
      showError('Выберите целевой инструмент');
      return;
    }

    try {
      setPassing(true);
      await tasksAPI.passToTool(passSourceTask._id, passTargetTool, passOptions, true);
      showSuccess(`Задача успешно передана в ${passTargetTool}`);
      setShowPassModal(false);
      fetchTasks();
      fetchStats();
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось передать задачу');
    } finally {
      setPassing(false);
    }
  };

  const handleExportToVulnerabilities = async (task) => {
    if (!task.result_id) {
      showError('Нет результата для экспорта');
      return;
    }

    if (!['nuclei', 'ffuf_parallel', 'headless'].includes(task.tool)) {
      showError('В уязвимости можно экспортировать только результаты nuclei, ffuf_parallel и headless');
      return;
    }
    
    const toolName = task.tool === 'nuclei' ? 'nuclei' : (task.tool === 'ffuf_parallel' ? 'ffuf_parallel' : 'headless');
    if (!window.confirm(`Экспортировать находки ${toolName} в коллекцию уязвимостей?`)) {
      return;
    }

    try {
      const response = await vulnerabilitiesAPI.exportFromNuclei(task.result_id);
      showSuccess(response.data.message);
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось экспортировать уязвимости');
    }
  };

  const handleDownloadGroupResults = async (group) => {
    try {
      // Download as ZIP archive from backend
      const response = await taskGroupsAPI.downloadArchive(group._id);
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `group_results_${group.name.replace(/[^a-zA-Z0-9]/g, '_')}_${group._id}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccess('ZIP-архив успешно скачан');
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось скачать результаты группы');
    }
  };

  const handleViewGroup = async (groupId) => {
    try {
      const response = await taskGroupsAPI.getGroup(groupId);
      setSelectedGroup(response.data);
      setGroupTasks(response.data.task_details || []);
      setShowGroupModal(true);
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось получить группу');
    }
  };

  const handleShowGraph = async (group) => {
    try {
      const response = await taskGroupsAPI.getGroup(group._id);
      const tasks = response.data.task_details || [];
      
      // Build graph from tasks with hierarchical vertical layout
      const nodes = [];
      const edges = [];
      
      // Create task map for quick lookup
      const taskMap = new Map();
      tasks.forEach((task) => {
        taskMap.set(task._id, task);
      });
      
      // Calculate hierarchical levels using topological sorting
      const calculateLevels = (tasks) => {
        const levels = new Map();
        const visited = new Set();
        const visiting = new Set();
        
        const visit = (taskId, currentLevel = 0) => {
          if (visiting.has(taskId)) {
            // Circular dependency - assign current level
            levels.set(taskId, currentLevel);
            return currentLevel;
          }
          if (visited.has(taskId)) {
            return levels.get(taskId) || currentLevel;
          }
          
          visiting.add(taskId);
          const task = taskMap.get(taskId);
          let maxChildLevel = currentLevel;
          
          if (task && task.input_data?.source_task_id) {
            const sourceTaskId = task.input_data.source_task_id;
            if (taskMap.has(sourceTaskId)) {
              const childLevel = visit(sourceTaskId, currentLevel + 1);
              maxChildLevel = Math.max(maxChildLevel, childLevel);
            }
          }
          
          visiting.delete(taskId);
          visited.add(taskId);
          levels.set(taskId, maxChildLevel);
          return maxChildLevel;
        };
        
        // Visit all tasks
        tasks.forEach(task => {
          if (!visited.has(task._id)) {
            visit(task._id);
          }
        });
        
        return levels;
      };
      
      const levels = calculateLevels(tasks);
      
      // Group tasks by level for horizontal positioning
      const tasksByLevel = new Map();
      tasks.forEach(task => {
        const level = levels.get(task._id) || 0;
        if (!tasksByLevel.has(level)) {
          tasksByLevel.set(level, []);
        }
        tasksByLevel.get(level).push(task);
      });
      
      // Create nodes with vertical hierarchical layout
      const maxLevel = Math.max(...Array.from(levels.values()));
      const levelSpacing = 200; // Vertical spacing between levels
      const nodeSpacing = 180; // Horizontal spacing between nodes in same level
      
      tasks.forEach((task) => {
        const level = levels.get(task._id) || 0;
        const levelTasks = tasksByLevel.get(level) || [];
        const indexInLevel = levelTasks.findIndex(t => t._id === task._id);
        
        // Calculate position
        const x = indexInLevel * nodeSpacing + (levelTasks.length - 1) * nodeSpacing / 2;
        const y = level * levelSpacing + 100;
        
        const metadata = getToolMetadata(task.tool);
        nodes.push({
          id: task._id,
          type: 'default',
          position: { x, y },
          data: {
            label: (
              <div style={{ padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', marginBottom: '4px' }}>{metadata?.icon || '🔧'}</div>
                <div style={{ fontWeight: 'bold', fontSize: '12px' }}>{task.tool}</div>
                <div style={{ fontSize: '10px', color: getStatusColor(task.status) }}>{task.status}</div>
                {task.source_tool && (
                  <div style={{ fontSize: '9px', color: '#666', marginTop: '2px' }}>
                    источник: {task.source_tool}
                  </div>
                )}
              </div>
            ),
          },
          style: {
            backgroundColor: metadata?.color ? `${metadata.color}20` : '#f5f5f5',
            border: `2px solid ${metadata?.color || '#999'}`,
            borderRadius: '8px',
            width: 150,
          },
        });
      });
      
      // Create edges based on source_task_id dependencies
      tasks.forEach((task) => {
        if (task.input_data?.source_task_id) {
          const sourceTaskId = task.input_data.source_task_id;
          if (taskMap.has(sourceTaskId)) {
            edges.push({
              id: `${sourceTaskId}-${task._id}`,
              source: sourceTaskId,
              target: task._id,
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#3b82f6',
              },
              style: { stroke: '#3b82f6', strokeWidth: 2 },
            });
          }
        }
      });
      
      setGraphNodes(nodes);
      setGraphEdges(edges);
      setSelectedGroup(response.data);
      setGroupTasks(tasks);
      setShowGraphModal(true);
    } catch (err) {
      showError(err.response?.data?.message || 'Не удалось построить граф');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock size={16} color="#f59e0b" />;
      case 'running': return <PlayCircle size={16} color="#3b82f6" />;
      case 'completed': return <CheckCircle size={16} color="#10b981" />;
      case 'failed': return <XCircle size={16} color="#ef4444" />;
      case 'cancelled': return <StopCircle size={16} color="#6b7280" />;
      default: return <AlertCircle size={16} color="#6b7280" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'running': return '#3b82f6';
      case 'completed': return '#10b981';
      case 'failed': return '#ef4444';
      case 'cancelled': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Ожидает',
      running: 'Выполняется',
      completed: 'Завершена',
      failed: 'Ошибка',
      cancelled: 'Отменена',
    };
    return labels[status] || status;
  };

  const getInputTypeLabel = (inputType) => {
    const labels = {
      manual: 'Ручной ввод',
      config: 'Конфиг',
      tool: 'Предыдущий пайплайн',
      pipeline: 'Из пайплайна',
      chained: 'Цепочка',
    };
    return labels[inputType] || inputType;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Н/Д';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0с';
    
    if (seconds < 60) {
      return `${Math.round(seconds)}с`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.round(seconds % 60);
      return remainingSeconds > 0 ? `${minutes}м ${remainingSeconds}с` : `${minutes}м`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = Math.round(seconds % 60);
      let result = `${hours}ч`;
      if (minutes > 0) result += ` ${minutes}м`;
      if (remainingSeconds > 0) result += ` ${remainingSeconds}с`;
      return result;
    }
  };

  if (loading && groups.length === 0) {
    return (
      <div className="loading">
        <div>Загрузка пайплайнов...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '28px' }}>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-icon" style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)' }}>
          <Layers size={24} color="white" />
        </div>
        <div className="page-header-text">
          <h1>Пайплайны</h1>
          <p>Управление и мониторинг пайплайнов сканирования</p>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '12px',
          marginBottom: '16px',
          backgroundColor: '#fee2e2',
          color: '#991b1b',
          borderRadius: '6px',
        }}>
          {error}
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}>
          <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
              {stats.total}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Всего</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#f59e0b' }}>
              {stats.pending}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Ожидают</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#3b82f6' }}>
              {stats.running}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Выполняются</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#10b981' }}>
              {stats.completed}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>Завершены</div>
          </div>
          <div className="card" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ef4444' }}>
              {stats.failed}
            </div>
            <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>С ошибкой</div>
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div className="card" style={{ marginBottom: '16px', padding: '16px' }}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ flex: '1', minWidth: '150px' }}>
            <label style={{
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              color: 'var(--text-primary)',
              marginBottom: '4px',
            }}>
              Фильтр по статусу
            </label>
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                fontSize: '14px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
              }}
            >
              <option value="">Все статусы</option>
              <option value="pending">Ожидает</option>
              <option value="running">Выполняется</option>
              <option value="completed">Завершена</option>
              <option value="failed">Ошибка</option>
              <option value="cancelled">Отменена</option>
            </select>
          </div>


          <button
            onClick={fetchTasks}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '8px 16px',
              border: 'none',
              borderRadius: '6px',
              backgroundColor: '#3b82f6',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              alignSelf: 'flex-end',
            }}
          >
            <RefreshCw size={16} />
            Обновить
          </button>
        </div>
      </div>

      {/* Pipelines Table */}
      <div className="card" style={{ padding: '0', overflow: 'hidden' }}>
        <table className="data-table" style={{ 
          width: '100%',
          borderCollapse: 'collapse',
          backgroundColor: 'var(--bg-secondary)',
          borderRadius: '8px',
          overflow: 'hidden',
          boxShadow: 'var(--shadow)',
          border: '1px solid var(--border-color)'
        }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--bg-tertiary)' }}>
              <th style={{ 
                padding: '16px 20px', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border-color)',
                width: '5%'
              }}></th>
              <th style={{ 
                padding: '16px 20px', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border-color)',
                width: '25%'
              }}>Название пайплайна</th>
              <th style={{ 
                padding: '16px 20px', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border-color)',
                width: '15%'
              }}>Статус</th>
              <th style={{ 
                padding: '16px 20px', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border-color)',
                width: '10%'
              }}>Задачи</th>
              <th style={{ 
                padding: '16px 20px', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border-color)',
                width: '20%'
              }}>Создан</th>
              <th style={{ 
                padding: '16px 20px', 
                textAlign: 'left', 
                fontWeight: '600', 
                color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border-color)',
                width: '15%'
              }}>Действия</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.map(group => {
              const isExpanded = expandedGroups.has(group._id);
              const tasks = group.task_details || [];
              // Use group metadata task counts if available, otherwise calculate from task_details
              const totalTasks = group.total_tasks || tasks.length;
              const completedTasks = group.completed_tasks || tasks.filter(t => t.status === 'completed').length;
              
              return (
                <React.Fragment key={group._id}>
                  {/* Group Row */}
                  <tr style={{ 
                    borderBottom: isExpanded ? 'none' : '1px solid var(--border-color)',
                    backgroundColor: isExpanded ? 'var(--bg-tertiary)' : 'transparent',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (!isExpanded) e.currentTarget.style.backgroundColor = 'var(--bg-tertiary)';
                  }}
                  onMouseLeave={(e) => {
                    if (!isExpanded) e.currentTarget.style.backgroundColor = 'transparent';
                  }}>
                    <td style={{ 
                      padding: '16px 20px',
                      borderBottom: isExpanded ? 'none' : '1px solid var(--border-color)'
                    }}>
                      {group.status !== 'completed' ? (
                        <button
                          onClick={() => toggleGroupExpansion(group._id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            color: 'var(--text-primary)'
                          }}
                        >
                          {isExpanded ? '▼' : '▶'}
                        </button>
                      ) : (
                        <div style={{
                          width: '20px',
                          height: '20px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'var(--text-secondary)'
                        }}>
                          ✓
                        </div>
                      )}
                    </td>
                    <td style={{ 
                      padding: '16px 20px',
                      borderBottom: isExpanded ? 'none' : '1px solid var(--border-color)'
                    }}>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '15px', color: 'var(--text-primary)' }}>
                          {group.name}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                          {tasks.map(t => t.tool).join(' → ')}
                        </div>
                      </div>
                    </td>
                    <td style={{ 
                      padding: '16px 20px',
                      borderBottom: isExpanded ? 'none' : '1px solid var(--border-color)'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '8px',
                        color: getStatusColor(group.status)
                      }}>
                        {getStatusIcon(group.status)}
                        <span style={{ fontSize: '14px' }}>{getStatusLabel(group.status)}</span>
                      </div>
                    </td>
                    <td style={{ 
                      padding: '16px 20px',
                      borderBottom: isExpanded ? 'none' : '1px solid var(--border-color)'
                    }}>
                      <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>
                        {completedTasks}/{totalTasks}
                      </span>
                    </td>
                    <td style={{ 
                      padding: '16px 20px',
                      borderBottom: isExpanded ? 'none' : '1px solid var(--border-color)',
                      fontSize: '14px',
                      color: 'var(--text-primary)'
                    }}>{formatDate(group.created_at)}</td>
                    <td style={{ 
                      padding: '16px 20px',
                      borderBottom: isExpanded ? 'none' : '1px solid var(--border-color)'
                    }}>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          onClick={() => handleShowGraph(group)}
                          style={{
                            padding: '6px 10px',
                            border: '1px solid #3b82f6',
                            borderRadius: '6px',
                            backgroundColor: 'transparent',
                            color: '#3b82f6',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#3b82f6';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#3b82f6';
                          }}
                          title="Показать граф"
                        >
                          <GitBranch size={14} />
                          Граф
                        </button>
                        <button
                          onClick={() => handleViewGroup(group._id)}
                          style={{
                            padding: '6px 10px',
                            border: '1px solid #8b5cf6',
                            borderRadius: '6px',
                            backgroundColor: 'transparent',
                            color: '#8b5cf6',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#8b5cf6';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#8b5cf6';
                          }}
                          title="Показать детали"
                        >
                          <Layers size={14} />
                          Детали
                        </button>
                        <button
                          onClick={() => handleDownloadGroupResults(group)}
                          style={{
                            padding: '6px 10px',
                            border: '1px solid #10b981',
                            borderRadius: '6px',
                            backgroundColor: 'transparent',
                            color: '#10b981',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#10b981';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#10b981';
                          }}
                          title="Скачать все результаты"
                        >
                          <Download size={14} />
                          Скачать все
                        </button>
                        <button
                          onClick={() => handleDeleteGroup(group._id)}
                          style={{
                            padding: '6px 10px',
                            border: '1px solid #ef4444',
                            borderRadius: '6px',
                            backgroundColor: 'transparent',
                            color: '#ef4444',
                            cursor: 'pointer',
                            fontSize: '12px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#ef4444';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.color = '#ef4444';
                          }}
                          title="Удалить"
                        >
                          <Trash2 size={14} />
                          Удалить
                        </button>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Expanded Tasks */}
                  {isExpanded && tasks && tasks.length > 0 && (() => {
                    const filteredTasks = tasks.filter(task => !statusFilter || task.status === statusFilter);
                    return filteredTasks.length > 0 ? filteredTasks.map((task, idx) => (
                    <tr key={task._id} style={{
                      backgroundColor: 'var(--bg-tertiary)',
                      borderBottom: idx === tasks.length - 1 ? '1px solid var(--border-color)' : 'none'
                    }}>
                      <td style={{ padding: '12px 20px', paddingLeft: '50px' }}>
                        <div style={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: getStatusColor(task.status)
                        }}></div>
                      </td>
                      <td style={{ padding: '12px 20px' }} colSpan="1">
                        <div style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-primary)' }}>
                          {task.tool}
                        </div>
                        <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                          {getInputTypeLabel(task.input_type)}
                        </div>
                      </td>
                      <td style={{ padding: '12px 20px' }}>
                        <span style={{ 
                          fontSize: '12px', 
                          color: getStatusColor(task.status),
                          textTransform: 'capitalize'
                        }}>
                          {getStatusLabel(task.status)}
                        </span>
                      </td>
                      <td style={{ padding: '12px 20px' }}>
                        {task.progress && task.progress.percentage > 0 ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '12px' }}>{task.progress.percentage}%</span>
                            <div style={{ 
                              width: '60px', 
                              height: '4px', 
                              backgroundColor: '#e5e7eb', 
                              borderRadius: '2px', 
                              overflow: 'hidden'
                            }}>
                              <div style={{ 
                                width: `${task.progress.percentage}%`, 
                                height: '100%', 
                                backgroundColor: '#3b82f6'
                              }}></div>
                            </div>
                          </div>
                        ) : (
                          <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>-</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 20px' }}>
                        <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {formatDate(task.created_at)}
                        </span>
                      </td>
                      <td style={{ padding: '12px 20px' }}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {task.result_id && (
                            <>
                              <button
                                onClick={() => handleViewResult(task)}
                                style={{
                                  padding: '4px 8px',
                                  border: '1px solid #3b82f6',
                                  borderRadius: '4px',
                                  backgroundColor: 'transparent',
                                  color: '#3b82f6',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <Eye size={12} />
                                Просмотр
                              </button>
                              <button
                                onClick={() => handleDownloadResult(task)}
                                style={{
                                  padding: '4px 8px',
                                  border: '1px solid #10b981',
                                  borderRadius: '4px',
                                  backgroundColor: 'transparent',
                                  color: '#10b981',
                                  cursor: 'pointer',
                                  fontSize: '11px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}
                              >
                                <Download size={12} />
                              </button>
                              {task.status === 'completed' && ['nuclei', 'ffuf_parallel', 'headless'].includes(task.tool) && (
                                <button
                                  onClick={() => handleExportToVulnerabilities(task)}
                                  style={{
                                    padding: '4px 8px',
                                    border: '1px solid #8b5cf6',
                                    borderRadius: '4px',
                                    backgroundColor: 'transparent',
                                    color: '#8b5cf6',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                  title="Экспорт в уязвимости"
                                >
                                  <Upload size={12} />
                                </button>
                              )}
                              {task.status === 'completed' && getCompatibleTools(task.tool).length > 0 && (
                                <button
                                  onClick={() => handlePassToTool(task)}
                                  style={{
                                    padding: '4px 8px',
                                    border: '1px solid #f59e0b',
                                    borderRadius: '4px',
                                    backgroundColor: 'transparent',
                                    color: '#f59e0b',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px'
                                  }}
                                >
                                  <ArrowRight size={12} />
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    )) : (
                      <tr>
                        <td colSpan="6" style={{ 
                          padding: '24px', 
                          textAlign: 'center', 
                          color: 'var(--text-secondary)',
                          fontStyle: 'italic'
                        }}>
                          Нет задач, подходящих под текущий фильтр
                        </td>
                      </tr>
                    );
                  })()}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>

        {filteredGroups.length === 0 && (
          <div style={{
            padding: '48px',
            textAlign: 'center',
            color: 'var(--text-secondary)',
          }}>
            Пайплайны не найдены
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          marginTop: '24px',
        }}>
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            style={{
              padding: '8px 16px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              opacity: page === 1 ? 0.5 : 1,
            }}
          >
            Назад
          </button>
          <span style={{ color: 'var(--text-secondary)' }}>
            Страница {page} из {Math.ceil(total / limit)}
          </span>
          <button
            onClick={() => setPage(Math.min(Math.ceil(total / limit), page + 1))}
            disabled={page >= Math.ceil(total / limit)}
            style={{
              padding: '8px 16px',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-primary)',
              cursor: page >= Math.ceil(total / limit) ? 'not-allowed' : 'pointer',
              opacity: page >= Math.ceil(total / limit) ? 0.5 : 1,
            }}
          >
            Далее
          </button>
        </div>
      )}

      {/* Result Modal */}
      {showTaskResultModal && taskResult && selectedTask && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold' }}>Результат задачи</h2>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {selectedTask && getCompatibleTools(selectedTask.tool).length > 0 && (
                  <button
                    onClick={() => {
                      setShowTaskResultModal(false);
                      handlePassToTool(selectedTask);
                    }}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #3b82f6',
                      borderRadius: '4px',
                      backgroundColor: 'transparent',
                      color: '#3b82f6',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <ArrowRight size={14} />
                    Передать в инструмент
                  </button>
                )}
                {selectedTask && selectedTask.result_id && (
                  <button
                    onClick={() => {
                      setShowTaskResultModal(false);
                      handleDownloadResult(selectedTask);
                    }}
                    style={{
                      padding: '6px 12px',
                      border: '1px solid #10b981',
                      borderRadius: '4px',
                      backgroundColor: 'transparent',
                      color: '#10b981',
                      cursor: 'pointer',
                      fontSize: '12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                    }}
                  >
                    <Download size={14} />
                    Скачать
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowTaskResultModal(false);
                    setTaskResult(null);
                    setSelectedTask(null);
                  }}
                  style={{
                    padding: '4px 12px',
                    border: 'none',
                    borderRadius: '4px',
                    backgroundColor: '#ef4444',
                    color: 'white',
                    cursor: 'pointer',
                  }}
                >
                  Закрыть
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Сводка</h3>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                <div>Всего элементов: {taskResult.summary?.total_items || 0}</div>
                <div>Успешно обработано: {taskResult.summary?.success_count || 0}</div>
                <div>Длительность: {formatDuration(taskResult.summary?.duration || 0)}</div>
              </div>
            </div>

            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px' }}>Результаты</h3>
              <ScannerResults
                result={taskResult}
                tool={selectedTask?.tool}
                onExportToVulnerabilities={['nuclei', 'ffuf_parallel', 'headless'].includes(selectedTask?.tool) ? () => {
                  setShowTaskResultModal(false);
                  handleExportToVulnerabilities(selectedTask);
                } : undefined}
                onDownload={() => {
                  setShowTaskResultModal(false);
                  handleDownloadResult(selectedTask);
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Pass To Tool Modal */}
      {showPassModal && passSourceTask && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1100,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ArrowRight size={24} color="#10b981" />
                Передача результатов в инструмент
              </h2>
              <button
                onClick={() => setShowPassModal(false)}
                disabled={passing}
                style={{
                  padding: '4px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  cursor: passing ? 'not-allowed' : 'pointer',
                  opacity: passing ? 0.5 : 1,
                }}
              >
                Закрыть
              </button>
            </div>

            <div style={{ marginBottom: '20px', padding: '12px', backgroundColor: 'var(--bg-tertiary)', borderRadius: '6px' }}>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                <strong>Исходная задача:</strong> {passSourceTask.tool}
              </div>
              {passSourceTask.source_tool && (
                <div style={{ fontSize: '14px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  <strong>Запущена из:</strong> {passSourceTask.source_tool}
                </div>
              )}
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--text-primary)',
                marginBottom: '8px',
              }}>
                Выберите целевой инструмент
              </label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                {getCompatibleTools(passSourceTask.tool).map((tool) => {
                  const metadata = getToolMetadata(tool);
                  return (
                    <button
                      key={tool}
                      onClick={() => setPassTargetTool(tool)}
                      style={{
                        padding: '16px',
                        border: `2px solid ${passTargetTool === tool ? metadata.color : 'var(--border-color)'}`,
                        borderRadius: '8px',
                        backgroundColor: passTargetTool === tool ? `${metadata.color}15` : 'var(--bg-tertiary)',
                        cursor: 'pointer',
                        textAlign: 'left',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div style={{ fontSize: '24px', marginBottom: '8px' }}>{metadata.icon}</div>
                      <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                        {metadata.name}
                      </div>
                      <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                        {metadata.description}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {passTargetTool && (
              <CustomFlagsInput
                tool={passTargetTool}
                value={passOptions}
                onChange={setPassOptions}
              />
            )}

            <div style={{
              display: 'flex',
              gap: '12px',
              marginTop: '24px',
              justifyContent: 'flex-end',
            }}>
              <button
                onClick={() => setShowPassModal(false)}
                disabled={passing}
                style={{
                  padding: '10px 20px',
                  border: '1px solid var(--border-color)',
                  borderRadius: '6px',
                  backgroundColor: 'var(--bg-tertiary)',
                  color: 'var(--text-primary)',
                  cursor: passing ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: passing ? 0.5 : 1,
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleConfirmPass}
                disabled={!passTargetTool || passing}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  borderRadius: '6px',
                  backgroundColor: '#10b981',
                  color: 'white',
                  cursor: !passTargetTool || passing ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  opacity: !passTargetTool || passing ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                {passing ? 'Передача...' : (
                  <>
                    <ArrowRight size={16} />
                    Передать в {passTargetTool ? getToolMetadata(passTargetTool).name : 'инструмент'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Graph View Modal */}
      {showGraphModal && selectedGroup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '90vw',
            width: '90%',
            maxHeight: '90vh',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <GitBranch size={24} color="#3b82f6" />
                Граф пайплайна: {selectedGroup.name}
              </h2>
              <button
                onClick={() => setShowGraphModal(false)}
                style={{
                  padding: '4px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                Закрыть
              </button>
            </div>

            <div style={{ flex: 1, border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
              <ReactFlow
                nodes={graphNodes}
                edges={graphEdges}
                fitView
                style={{
                  backgroundColor: 'var(--bg-primary)',
                }}
              >
                <Controls />
                <MiniMap
                  style={{
                    backgroundColor: 'var(--bg-tertiary)',
                  }}
                />
                <Background variant="dots" gap={12} size={1} />
              </ReactFlow>
            </div>

            <div style={{
              marginTop: '16px',
              padding: '12px',
              backgroundColor: 'var(--bg-tertiary)',
              borderRadius: '6px',
            }}>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                <div><strong>Всего задач:</strong> {groupTasks.length}</div>
                <div><strong>Завершено:</strong> {groupTasks.filter(t => t.status === 'completed').length}</div>
                <div><strong>Выполняется:</strong> {groupTasks.filter(t => t.status === 'running').length}</div>
                <div><strong>Ожидает:</strong> {groupTasks.filter(t => t.status === 'pending').length}</div>
                <div><strong>С ошибкой:</strong> {groupTasks.filter(t => t.status === 'failed').length}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Task Group Modal */}
      {showGroupModal && selectedGroup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '900px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'auto',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Layers size={24} color="#8b5cf6" />
                Группа задач: {selectedGroup.name}
              </h2>
              <button
                onClick={() => setShowGroupModal(false)}
                style={{
                  padding: '4px 12px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  cursor: 'pointer',
                }}
              >
                Закрыть
              </button>
            </div>

            <div style={{
              padding: '12px',
              backgroundColor: 'var(--bg-tertiary)',
              borderRadius: '6px',
              marginBottom: '16px',
            }}>
              <div style={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
                <div><strong>Статус:</strong> <span style={{ color: getStatusColor(selectedGroup.status) }}>{getStatusLabel(selectedGroup.status)}</span></div>
                <div style={{ marginTop: '4px' }}><strong>Создана:</strong> {formatDate(selectedGroup.created_at)}</div>
                <div style={{ marginTop: '4px' }}><strong>Задач:</strong> {groupTasks.length}</div>
              </div>
            </div>

            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>Задачи в группе</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {groupTasks.map((task, index) => (
                <div
                  key={task._id}
                  style={{
                    padding: '16px',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    backgroundColor: 'var(--bg-tertiary)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        backgroundColor: '#8b5cf6',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                      }}>
                        {index + 1}
                      </div>
                      <div>
                        <div style={{ fontSize: '16px', fontWeight: '600', color: 'var(--text-primary)' }}>
                          {task.tool}
                        </div>
                        <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                          {task.input_type}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: getStatusColor(task.status) }}>
                      {getStatusIcon(task.status)}
                      <span style={{ fontSize: '14px' }}>{getStatusLabel(task.status)}</span>
                    </div>
                  </div>

                  {task.input_data?.source_tool && (
                    <div style={{
                      padding: '8px 12px',
                      backgroundColor: 'var(--bg-secondary)',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                    }}>
                      <ArrowRight size={12} style={{ display: 'inline', marginRight: '4px' }} />
                      Получает данные из: <strong>{task.input_data.source_tool}</strong>
                    </div>
                  )}

                  {task.progress && task.progress.percentage > 0 && (
                    <div style={{ marginTop: '8px' }}>
                      <div style={{ fontSize: '12px', marginBottom: '4px', color: 'var(--text-secondary)' }}>
                        Прогресс: {task.progress.percentage}%
                      </div>
                      <div style={{
                        width: '100%',
                        height: '6px',
                        backgroundColor: '#e5e7eb',
                        borderRadius: '3px',
                        overflow: 'hidden',
                      }}>
                        <div style={{
                          width: `${task.progress.percentage}%`,
                          height: '100%',
                          backgroundColor: '#3b82f6',
                          transition: 'width 0.3s',
                        }}></div>
                      </div>
                    </div>
                  )}

                  {task.status === 'failed' && task.error && (
                    <div style={{
                      marginTop: '12px',
                      padding: '12px',
                      backgroundColor: '#fef2f2',
                      border: '1px solid #fecaca',
                      borderRadius: '6px',
                      fontSize: '12px',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <AlertCircle size={16} color="#dc2626" />
                        <span style={{ fontWeight: '600', color: '#dc2626' }}>Ошибка задачи</span>
                      </div>
                      <div style={{ color: '#7f1d1d', marginBottom: '8px' }}>
                        {task.error}
                      </div>
                      {task.error_details && (
                        <details style={{ color: '#7f1d1d' }}>
                          <summary style={{ cursor: 'pointer', fontWeight: '500', marginBottom: '4px' }}>
                            Технические детали
                          </summary>
                          <div style={{ 
                            marginTop: '8px', 
                            padding: '8px', 
                            backgroundColor: '#fef7f7', 
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            fontSize: '11px',
                            whiteSpace: 'pre-wrap'
                          }}>
                            {task.error_details.error_message && (
                              <div><strong>Ошибка:</strong> {task.error_details.error_message}</div>
                            )}
                            {task.error_details.return_code !== undefined && (
                              <div><strong>Код возврата:</strong> {task.error_details.return_code}</div>
                            )}
                            {task.error_details.stderr && (
                              <div><strong>Stderr:</strong> {task.error_details.stderr}</div>
                            )}
                            {task.error_details.command_executed && (
                              <div><strong>Команда:</strong> {task.error_details.command_executed}</div>
                            )}
                          </div>
                        </details>
                      )}
                    </div>
                  )}

                  {task.result_id && (
                    <button
                      onClick={() => handleViewResult(task)}
                      style={{
                        marginTop: '12px',
                        padding: '6px 12px',
                        border: '1px solid #3b82f6',
                        borderRadius: '4px',
                        backgroundColor: 'transparent',
                        color: '#3b82f6',
                        cursor: 'pointer',
                        fontSize: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <Eye size={14} />
                      Показать результат
                    </button>
                  )}
                </div>
              ))}
            </div>

            {groupTasks.length > 1 && (
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#eff6ff',
                border: '1px solid #3b82f6',
                borderRadius: '6px',
              }}>
                <div style={{ fontSize: '13px', color: '#1e40af' }}>
                  <strong>💡 Поток пайплайна:</strong> {groupTasks.map(t => t.tool).join(' → ')}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Tasks;
